import {
  getLatestArableFieldTilesFromArray,
  getLatestFieldTilesFromArray,
  Tile,
  useArableFields,
  useFields,
  useTilesByIds,
} from "model";
import { useEffect, useMemo } from "react";
import { Layer, Source } from "react-map-gl/mapbox";
import { useUIStore } from "stores";

// Zoom threshold constant - kept for backwards compatibility but no longer used
// to hide tile layers. Tiles are now shown at all zoom levels.
export const TILE_ZOOM_THRESHOLD = 14;

export default function FieldTileLayers({
  editedFieldId,
  fieldIds,
  zoom,
  onTilesReadyChange,
}: {
  editedFieldId?: string;
  fieldIds?: string[];
  zoom?: number;
  /** Callback to notify parent when tiles are ready (loaded and have data to display) */
  onTilesReadyChange?: (ready: boolean) => void;
}) {
  // Fetch fields and arable fields
  const { data: fields = [], isLoading: fieldsLoading } = useFields();
  const { data: arableFields = [], isLoading: arableFieldsLoading } =
    useArableFields();
  const tmpDeletedFeatures = useUIStore((state) => state.tmpDeletedFeatures);

  // Get only the latest tile ID for each field (not all historical tiles)
  const allTileIds = useMemo(() => {
    const fieldTileIds = getLatestFieldTilesFromArray(fields);
    const arableFieldTileIds = getLatestArableFieldTilesFromArray(arableFields);
    return [...fieldTileIds, ...arableFieldTileIds];
  }, [fields, arableFields]);

  // Fetch tiles using the reactive hook
  const { data: fetchedTiles, isLoading: tilesLoading } =
    useTilesByIds(allTileIds);

  // Filter by not deleted boundaries
  const allTiles = useMemo(() => {
    return fetchedTiles.filter(
      (t) => t.field && tmpDeletedFeatures.indexOf(t.field) === -1
    );
  }, [fetchedTiles, tmpDeletedFeatures]);

  // Consider loading if fields are loading OR tiles are loading
  const isLoading = fieldsLoading || arableFieldsLoading || tilesLoading;

  // Tiles are "ready" when loading is complete
  // If there are no tiles to display, we're still "ready" (nothing to wait for)
  const tilesReady = !isLoading;

  // Notify parent of tiles ready state changes
  useEffect(() => {
    onTilesReadyChange?.(tilesReady);
  }, [tilesReady, onTilesReadyChange]);

  // Memoize filtered tiles to prevent recalculation on every render
  const tiles = useMemo(() => {
    return allTiles.filter((t) => {
      // Always exclude edited field
      if (t.field === editedFieldId) return false;

      // If fieldIds is provided, only include tiles from those fields
      if (fieldIds && fieldIds.length > 0) {
        return fieldIds.includes(t.field || "");
      }

      // Otherwise include all tiles
      return true;
    });
  }, [allTiles, editedFieldId, fieldIds]);

  return (
    <>
      {tiles.map((t: Tile) => (
        <Source
          id={t.guid}
          key={t.guid + "s"}
          type={t.type}
          tiles={t.tiles}
          scheme={t.scheme}
          bounds={t.bounds}
          tileSize={t.tileSize}
          minzoom={t.minzoom}
          maxzoom={t.maxzoom}
        >
          <Layer
            id={t.guid}
            type={"raster"}
            key={t.guid + "l"}
            layout={{
              visibility: t.visibility,
            }}
            paint={{}}
            beforeId="field-fill" // do we have to also add "arable-field-fill"?
          />
        </Source>
      ))}
    </>
  );
}
